import React from "react";
import {
  Box,
  EntityChipShell,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import { resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { EntityVisual } from "./entity-visual";
import type { Omittable } from "@salestrak/ui";

export type EntityBreadcrumbLinkProps = Omittable<{
  entity: EntityData;
  onClick?: () => void;
  active?: boolean;
  /**
   * Icon-only crumb: drops the label to cede the trail to the segments
   * beside it, with the full name on a hover tooltip (and kept for screen
   * readers). The glyph stays on the canonical entity-visual path.
   */
  collapsed?: boolean;
  /**
   * The crumb is the pressed face of a wrapper-owned dropdown — the ACTIVE
   * crumb's management menu, where a routing press has nowhere to go. It
   * wears the hover wash (held while that menu is open), takes the pointer
   * cursor the press earns, and appends a trailing chevron so the menu reads
   * before it opens. Inert crumbs never carry the chevron: a non-active
   * crumb is a plain routing link.
   */
  dropdown?: boolean;
}>;

// The entity system's breadcrumb segment: keeps the glyph and label on the
// canonical resolution path rather than decomposing a visual into BreadcrumbLink
// icon/iconColor primitives. Renders through the entity shell at `size="crumb"`
// — the 24px ghost pill with the 4px inset and 4px glyph↔label gap that every
// crumb shape (plain BreadcrumbLink, dropdown, icon-only) shares.
// Visual contract for the crumb: every glyph occupies a 16px box. A bare icon
// renders at 16px (`bareIconSize`), while an identity visual uses the shared
// compact footprint. Both treatments align beside 14px labels. The
// active (current) crumb reads at the view-title weight, 14px/600.
export function EntityBreadcrumbLink({
  entity,
  onClick,
  active,
  collapsed,
  dropdown,
}: EntityBreadcrumbLinkProps) {
  const label = resolveEntityLabel(entity);

  if (collapsed) {
    return (
      <Tooltip>
        {/* The shell is the pressable (a role="button" Box), so the trigger
            renders as a plain wrapper around it — a button element here would
            nest interactive content inside interactive content. */}
        <TooltipTrigger render={<Box direction="row" align="center" />}>
          <EntityChipShell
            as="box"
            size="crumb"
            onClick={onClick}
            hoverWash={dropdown ? "press" : undefined}
          >
            <EntityVisual entity={entity} size="2xs" bareIconSize="md" />
            <Text visuallyHidden>{label}</Text>
          </EntityChipShell>
        </TooltipTrigger>
        <TooltipPopup side="bottom">{label}</TooltipPopup>
      </Tooltip>
    );
  }

  return (
    <EntityChipShell
      as="box"
      size="crumb"
      onClick={onClick}
      hoverWash={dropdown ? "press" : undefined}
    >
      <EntityVisual entity={entity} size="2xs" bareIconSize="md" />
      <Text
        size="sm"
        weight={active ? "semibold" : "normal"}
        variant={active ? undefined : "muted"}
        truncate={1}
      >
        {label}
      </Text>
      {/* The chip-scale dropdown chevron (the filter-value-chip rung). */}
      {dropdown ? <Icon name="chevron-down" size="xs" color="muted" /> : null}
    </EntityChipShell>
  );
}
